import React, { useState } from 'react';
import { Box, SxProps } from '@mui/material';
import { useStacklineTheme } from '@stackline/ui';
import { IconMore, IconLess } from 'src/components/SvgIcons';

export interface SlFlippableCardProps {
  sx?: SxProps;
  flipContainerSx?: SxProps;
  cardFront: (props: { isHovered: boolean }) => React.ReactNode;
  cardBack: (props: { isHovered: boolean }) => React.ReactNode;
  flipIconSize?: string;
}

const FLIP_TIME = 0.4;

/**
 * Generic flippable card component. Provide the front and back
 * content as functions.
 */
export function SlFlippableCard({
  sx,
  flipContainerSx,
  cardFront,
  cardBack,
  flipIconSize = '24px'
}: SlFlippableCardProps) {
  const theme = useStacklineTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [side, setSide] = useState<'front' | 'back'>('front');

  // Controls card content - used to hide content while the flip animation happens
  const [cardSide, setCardSide] = useState<'front' | 'back'>('front');

  const flipCard = () => {
    const newSide = side === 'front' ? 'back' : 'front';
    setCardSide(null);
    setSide(newSide);
    setTimeout(() => setCardSide(newSide), (FLIP_TIME * 1000) / 2);
  };

  return (
    <Box
      sx={{
        ...{
          border: `1px solid ${theme.colors.primaryGray}`,
          borderRadius: '8px',
          transform: side === 'back' ? 'rotateY(180deg)' : 'rotateY(0deg)',
          transition: `${FLIP_TIME}s`
        },
        ...sx
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isHovered && cardSide !== null && (
        <Box
          sx={{
            ...{
              position: 'absolute',
              top: '16px',
              display: 'inline-block',
              cursor: 'pointer',
              zIndex: 1,
              ...(cardSide === 'front'
                ? {
                    right: '16px'
                  }
                : {
                    left: '16px' // We rotate Y by 180 degree on the back side, so we need to change the right to left
                  })
            },
            ...flipContainerSx
          }}
        >
          {cardSide === 'front' ? (
            <IconMore height={flipIconSize} width={flipIconSize} onClick={() => flipCard()} />
          ) : (
            <IconLess height={flipIconSize} width={flipIconSize} onClick={() => flipCard()} />
          )}
        </Box>
      )}
      <div
        style={{
          height: '100%',
          width: '100%',
          ...(cardSide === 'back'
            ? {
                transform: 'rotateY(180deg)'
              }
            : {})
        }}
      >
        {cardSide === 'front' ? cardFront({ isHovered }) : cardSide === 'back' ? cardBack({ isHovered }) : null}
      </div>
    </Box>
  );
}
